import React, { useState } from 'react';
import {
  User,
  Calendar,
  MapPin,
  Clock,
  Compass,
  Briefcase,
  Award,
  FileText,
  Zap,
  ArrowLeft,
  Plus,
  Edit3,
  Phone,
  Mail,
  Shield,
  Tag,
  CheckCircle2
} from 'lucide-react';
import { PracticeClient, PracticeCase, PracticeConsultation, PracticeReport } from '../../types/practiceManagement';
import { Language, CompleteKundali } from '../../types/kundali';
import { ClientTimeline } from './ClientTimeline';
import { getClientTimeline, getPracticeCases, getPracticeConsultations, getPracticeReports } from '../../lib/practiceManagementService';

interface ClientProfileProps {
  client: PracticeClient;
  lang: Language;
  onBack: () => void;
  onOpenKundali: (client: PracticeClient) => void;
  onNewCaseForClient: (client: PracticeClient) => void;
  onNewConsultationForClient: (client: PracticeClient) => void;
  onGenerateReportForClient: (client: PracticeClient) => void;
  onOpenCaseWorkspace: (caseItem: PracticeCase) => void;
}

export const ClientProfile: React.FC<ClientProfileProps> = ({
  client,
  lang,
  onBack,
  onOpenKundali,
  onNewCaseForClient,
  onNewConsultationForClient,
  onGenerateReportForClient,
  onOpenCaseWorkspace
}) => {
  const isHi = lang === 'hi';
  const [activeTab, setActiveTab] = useState<'cases' | 'timeline' | 'consultations' | 'notes'>('cases');

  const timelineEvents = getClientTimeline(client.id);
  const clientCases = getPracticeCases().filter((c) => c.clientId === client.id);
  const clientConsultations = getPracticeConsultations().filter((c) => c.clientId === client.id);
  const clientReports = getPracticeReports().filter((r) => r.clientId === client.id);

  return (
    <div className="space-y-4">
      {/* Client Header Card */}
      <div className="bg-white dark:bg-slate-850 p-5 rounded-xl border border-astro-border/90 dark:border-astro-strong-border/80 shadow-xs space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-3.5">
            <button
              onClick={onBack}
              className="p-2 rounded-lg bg-astro-secondary-surface dark:bg-astro-secondary hover:bg-astro-card dark:hover:bg-astro-secondary text-astro-secondary-text dark:text-astro-muted transition-colors cursor-pointer mt-0.5"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>

            <div className="space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-lg font-black text-astro-text dark:text-astro-muted">
                  {client.name}
                </h2>
                <span className="text-xs font-mono font-bold text-[#52635F] dark:text-amber-400 bg-astro-secondary-surface dark:bg-amber-950/40 px-2 py-0.5 rounded border border-[#E2DED5] dark:border-amber-800">
                  {client.referenceId}
                </span>
                <span
                  className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                    client.status === 'ACTIVE'
                      ? 'bg-emerald-500/15 text-emerald-800 dark:text-emerald-300'
                      : 'bg-astro-secondary-surface0/15 text-astro-secondary dark:text-amber-300'
                  }`}
                >
                  {client.status}
                </span>
              </div>

              {/* Natal Coordinates & Core Facts */}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-astro-secondary-text dark:text-astro-muted">
                <span className="flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5 text-astro-muted" />
                  <strong>DOB:</strong> {client.dateOfBirth} ({client.ageYears}y)
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5 text-astro-muted" />
                  <strong>TOB:</strong> {client.timeOfBirth}
                </span>
                <span className="flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5 text-astro-muted" />
                  {client.placeOfBirth}
                </span>
              </div>

              {/* Astrological Core Badges */}
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-astro-secondary-surface dark:bg-astro-secondary text-astro-secondary-text dark:text-astro-muted">
                  Lagna: {client.lagnaSign}
                </span>
                <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-astro-secondary-surface dark:bg-astro-secondary text-astro-secondary-text dark:text-astro-muted">
                  Moon: {client.moonSign} ({client.nakshatra})
                </span>
                <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-astro-secondary-surface dark:bg-amber-950/40 text-astro-secondary dark:text-amber-200 border border-[#E2DED5] dark:border-amber-700">
                  Dasha: {client.activeDasha}
                </span>
                <span className="text-[11px] font-medium text-astro-muted">
                  Stage: {client.lifeStage}
                </span>
              </div>
            </div>
          </div>

          {/* Quick Action Button Group */}
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => onOpenKundali(client)}
              className="min-h-[38px] px-3.5 py-1.5 rounded-lg bg-astro-secondary-surface0 hover:bg-astro-secondary text-white text-xs font-bold flex items-center gap-1.5 shadow-xs transition-all cursor-pointer"
            >
              <Compass className="w-4 h-4" />
              <span>{isHi ? 'कुंडली खोलें' : 'Open Kundali'}</span>
            </button>

            <button
              onClick={() => onNewCaseForClient(client)}
              className="min-h-[38px] px-3 py-1.5 rounded-lg bg-astro-secondary-surface dark:bg-astro-secondary hover:bg-astro-secondary-surface0/10 hover:text-[#52635F] dark:hover:text-amber-300 text-astro-secondary-text dark:text-astro-muted text-xs font-bold flex items-center gap-1.5 border border-astro-border dark:border-astro-strong-border transition-colors cursor-pointer"
            >
              <Briefcase className="w-3.5 h-3.5" />
              <span>{isHi ? '+ नया केस' : '+ New Case'}</span>
            </button>

            <button
              onClick={() => onNewConsultationForClient(client)}
              className="min-h-[38px] px-3 py-1.5 rounded-lg bg-astro-secondary-surface dark:bg-astro-secondary hover:bg-orange-500/10 hover:text-orange-700 dark:hover:text-orange-300 text-astro-secondary-text dark:text-astro-muted text-xs font-bold flex items-center gap-1.5 border border-astro-border dark:border-astro-strong-border transition-colors cursor-pointer"
            >
              <Award className="w-3.5 h-3.5" />
              <span>{isHi ? '+ परामर्श' : '+ Consult'}</span>
            </button>

            <button
              onClick={() => onGenerateReportForClient(client)}
              className="min-h-[38px] px-3 py-1.5 rounded-lg bg-astro-secondary-surface dark:bg-astro-secondary hover:bg-indigo-500/10 hover:text-indigo-700 dark:hover:text-indigo-300 text-astro-secondary-text dark:text-astro-muted text-xs font-bold flex items-center gap-1.5 border border-astro-border dark:border-astro-strong-border transition-colors cursor-pointer"
            >
              <FileText className="w-3.5 h-3.5" />
              <span>{isHi ? 'रिपोर्ट बनाएं' : 'Report'}</span>
            </button>
          </div>
        </div>

        {/* Timing Alert Banner if active */}
        {client.hasUpcomingTimingAlert && (
          <div className="p-3 bg-astro-secondary-surface0/10 border border-astro-accent/30 rounded-lg flex items-center justify-between gap-2 text-xs text-astro-secondary dark:text-amber-200">
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-amber-600 shrink-0" />
              <span>
                <strong>Upcoming Timing Confluence:</strong> {client.upcomingTimingSummary}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Tabs Switcher */}
      <div className="flex items-center gap-2 border-b border-astro-border dark:border-astro-strong-border pb-2">
        <button
          onClick={() => setActiveTab('cases')}
          className={`px-3.5 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer ${
            activeTab === 'cases'
              ? 'bg-astro-secondary-surface0 text-white shadow-xs'
              : 'text-astro-secondary-text dark:text-astro-muted hover:bg-astro-secondary-surface dark:hover:bg-astro-secondary'
          }`}
        >
          {isHi ? 'केस व प्रकरण' : 'Cases & Readings'} ({clientCases.length})
        </button>

        <button
          onClick={() => setActiveTab('timeline')}
          className={`px-3.5 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer ${
            activeTab === 'timeline'
              ? 'bg-astro-secondary-surface0 text-white shadow-xs'
              : 'text-astro-secondary-text dark:text-astro-muted hover:bg-astro-secondary-surface dark:hover:bg-astro-secondary'
          }`}
        >
          {isHi ? 'दीर्घकालिक इतिहास (Timeline)' : 'Longitudinal Timeline'} ({timelineEvents.length})
        </button>

        <button
          onClick={() => setActiveTab('consultations')}
          className={`px-3.5 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer ${
            activeTab === 'consultations'
              ? 'bg-astro-secondary-surface0 text-white shadow-xs'
              : 'text-astro-secondary-text dark:text-astro-muted hover:bg-astro-secondary-surface dark:hover:bg-astro-secondary'
          }`}
        >
          {isHi ? 'परामर्श इतिहास' : 'Consultations'} ({clientConsultations.length})
        </button>

        <button
          onClick={() => setActiveTab('notes')}
          className={`px-3.5 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer ${
            activeTab === 'notes'
              ? 'bg-astro-secondary-surface0 text-white shadow-xs'
              : 'text-astro-secondary-text dark:text-astro-muted hover:bg-astro-secondary-surface dark:hover:bg-astro-secondary'
          }`}
        >
          {isHi ? 'क्लाइंट नोट्स' : 'Profile Notes'}
        </button>
      </div>

      {/* Tab Panels */}
      {activeTab === 'cases' && (
        <div className="space-y-3">
          {clientCases.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
              {clientCases.map((c) => (
                <div
                  key={c.id}
                  onClick={() => onOpenCaseWorkspace(c)}
                  className="bg-white dark:bg-slate-850 p-4 rounded-xl border border-astro-border/90 dark:border-astro-strong-border/80 shadow-xs hover:shadow-md transition-all hover:border-amber-400/50 cursor-pointer space-y-2"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-mono font-bold text-[#52635F] dark:text-amber-400">
                      {c.caseNumber}
                    </span>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-astro-secondary-surface dark:bg-astro-secondary text-astro-secondary-text dark:text-astro-muted">
                      {c.stage}
                    </span>
                  </div>
                  <div className="text-xs font-bold text-astro-text dark:text-astro-muted">
                    {c.themeTitleEn}
                  </div>
                  <div className="text-xs text-astro-secondary-text dark:text-astro-muted line-clamp-2 italic">
                    "{c.targetQuestion}"
                  </div>
                  <div className="text-[11px] text-astro-muted dark:text-astro-muted pt-1 border-t border-astro-border dark:border-astro-strong-border flex justify-between">
                    <span>Practitioner: {c.assignedPractitioner}</span>
                    <span>{c.lastActivityDate}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center bg-white dark:bg-slate-850 rounded-xl border border-astro-border/80 dark:border-astro-strong-border/80 space-y-2">
              <p className="text-xs text-astro-muted">No active cases for this client yet.</p>
              <button
                onClick={() => onNewCaseForClient(client)}
                className="px-3 py-1.5 rounded-lg bg-astro-secondary-surface0 text-white font-bold text-xs cursor-pointer"
              >
                + Open First Case
              </button>
            </div>
          )}
        </div>
      )}

      {activeTab === 'timeline' && (
        <ClientTimeline events={timelineEvents} lang={lang} />
      )}

      {activeTab === 'consultations' && (
        <div className="space-y-3">
          {clientConsultations.length > 0 ? (
            clientConsultations.map((con) => (
              <div
                key={con.id}
                className="bg-white dark:bg-slate-850 p-4 rounded-xl border border-astro-border/90 dark:border-astro-strong-border/80 shadow-xs flex items-start justify-between gap-3 text-xs"
              >
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-bold text-[#52635F] dark:text-amber-400">
                      {con.consultationNumber}
                    </span>
                    <span className="font-bold text-astro-text dark:text-astro-muted">
                      {con.meetingFormat} Session ({con.durationMinutes} min)
                    </span>
                  </div>
                  <div className="text-astro-secondary-text dark:text-astro-muted">{con.question}</div>
                  <div className="text-[11px] text-astro-muted font-mono">
                    Scheduled: {con.scheduledDateTime}
                  </div>
                </div>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-astro-secondary-surface dark:bg-astro-secondary text-astro-secondary-text dark:text-astro-muted">
                  {con.status}
                </span>
              </div>
            ))
          ) : (
            <div className="p-8 text-center bg-white dark:bg-slate-850 rounded-xl border border-astro-border/80 dark:border-astro-strong-border/80 text-xs text-astro-muted">
              No previous consultations recorded.
            </div>
          )}
        </div>
      )}

      {activeTab === 'notes' && (
        <div className="bg-white dark:bg-slate-850 p-4 rounded-xl border border-astro-border/90 dark:border-astro-strong-border/80 space-y-2 text-xs">
          <h4 className="font-bold text-astro-text dark:text-astro-muted">General Client Notes:</h4>
          <p className="text-astro-secondary-text dark:text-astro-muted bg-astro-secondary-surface dark:bg-astro-primary p-3 rounded-lg border border-astro-border dark:border-astro-strong-border">
            {client.notes || 'No general notes recorded for this client.'}
          </p>
        </div>
      )}
    </div>
  );
};
